import api from './api';

// Thin wrapper around backend PDF endpoints.
export const pdfOps = {
  meta: (docId) => api.get(`/pdf/${docId}/meta`).then(r => r.data),

  pageImageUrl: (docId, page, dpi = 120) => {
    return `${api.defaults.baseURL}/pdf/${docId}/page/${page}.png?dpi=${dpi}&_t=${Date.now()}`;
  },

  fetchPageImage: async (docId, page, dpi = 120) => {
    const r = await fetch(`${api.defaults.baseURL}/pdf/${docId}/page/${page}.png?dpi=${dpi}`, {
      credentials: 'include',
    });
    if (!r.ok) throw new Error('Failed to render page');
    const blob = await r.blob();
    return URL.createObjectURL(blob);
  },

  downloadPdfBlob: async (docId) => {
    const r = await fetch(`${api.defaults.baseURL}/documents/${docId}/download`, {
      credentials: 'include',
    });
    return r.blob();
  },

  text: (docId, withPositions = false) =>
    api.get(`/pdf/${docId}/text`, { params: { with_positions: withPositions } }).then(r => r.data),

  apply: (docId, op, params = {}) =>
    api.post(`/pdf/${docId}/apply`, { op, params }).then(r => r.data),

  reorder: (docId, order) => pdfOps.apply(docId, 'reorder', { order }),
  rotate: (docId, rotations) => pdfOps.apply(docId, 'rotate', { rotations }),
  deletePages: (docId, indices) => pdfOps.apply(docId, 'delete_pages', { indices }),
  insertBlank: (docId, at, width = 595, height = 842) =>
    pdfOps.apply(docId, 'insert_blank', { at, width, height }),
  watermarkText: (docId, text, opts = {}) =>
    pdfOps.apply(docId, 'watermark_text', { text, ...opts }),
  bates: (docId, opts) => pdfOps.apply(docId, 'bates', opts),
  headerFooter: (docId, opts) => pdfOps.apply(docId, 'header_footer', opts),
  redact: (docId, regions) => pdfOps.apply(docId, 'redact', { regions }),
  encrypt: (docId, password, ownerPassword) =>
    pdfOps.apply(docId, 'encrypt', { password, ownerPassword }),
  decrypt: (docId, password) => pdfOps.apply(docId, 'decrypt', { password }),
  setMetadata: (docId, metadata) => pdfOps.apply(docId, 'set_metadata', { metadata }),
  fillForm: (docId, values, flatten = false) =>
    pdfOps.apply(docId, 'fill_form', { values, flatten }),
  flatten: (docId) => pdfOps.apply(docId, 'flatten'),
  compress: (docId, imageQuality = 60) => pdfOps.apply(docId, 'compress', { imageQuality }),
  ocr: (docId, language = 'eng') => pdfOps.apply(docId, 'ocr', { language }),
  applyEdits: (docId, edits) => pdfOps.apply(docId, 'apply_edits', { edits }),
  extractSpans: (docId, page) => pdfOps.apply(docId, 'extract_spans', { page }),
  replaceSpans: (docId, edits) => pdfOps.apply(docId, 'replace_spans', { edits }),
  replaceText: (docId, opts) => pdfOps.apply(docId, 'replace_text', opts),
  crop: (docId, crops) => pdfOps.apply(docId, 'crop', { crops }),
  extractPages: (docId, indices) => pdfOps.apply(docId, 'extract_pages', { indices }),
  addLinks: (docId, links) => pdfOps.apply(docId, 'add_links', { links }),
  deleteAnnotations: (docId, targets = [], all = false) =>
    pdfOps.apply(docId, 'delete_annotations', { targets, all }),
  watermarkImage: (docId, dataUrl, opts = {}) =>
    pdfOps.apply(docId, 'watermark_image', { dataUrl, ...opts }),

  split: (docId, ranges) => api.post(`/pdf/${docId}/split`, { ranges }).then(r => r.data),
  merge: (documentIds, opts = {}) =>
    api.post('/pdf/merge', { documentIds, ...opts }).then(r => r.data),
  compare: (docId, otherId) => api.post(`/pdf/${docId}/compare/${otherId}`).then(r => r.data),

  exportUrl: (docId, fmt) => `${api.defaults.baseURL}/pdf/${docId}/export/${fmt}`,
};

export default pdfOps;
